import {Location, NgClass} from '@angular/common';
import {Component, HostBinding, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {Brand, VehicleType} from '@service-and-repairs/awpintegrationlib';
import {ServiceCaseHolder} from '../../core/service-case/models/service-case-holder';
import {VehicleLoader} from '../../core/vehicle/services/vehicle.loader';
import {IWebComponentAppConfiguration} from '../../interfaces/IWebComponentAppConfiguration';
import {WebComponentService} from './web-component.service';
import {DsMessageModule, DsProgressCircleModule} from '@bmw-ds/components';
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-web-component',
  templateUrl: './web-component.component.html',
  styleUrls: ['./web-component.component.scss'],
  imports: [NgClass, DsMessageModule, DsProgressCircleModule, TranslateModule]
})
/**
 * This component is created by Angular routing if a web component app is selected. It will only exist while a web
 * component is shown, the actual web components exist separately and will not be destroyed once created. This
 * component manages visibility and error/loading indicators. It uses the WebComponentService to actually manage the
 * web components.
 */
export class WebComponentComponent implements OnInit, OnDestroy {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly location: Location = inject(Location);
  private readonly webComponentService: WebComponentService = inject(WebComponentService);

  @HostBinding('class.visible')
  showErrorOrLoadingIndicator: boolean;

  appConfiguration: IWebComponentAppConfiguration;
  additionalAttributes: object;

  loadingIndicatorVisible: boolean = false;
  errorIndicatorVisible: boolean = false;
  serviceCaseRequiredErrorVisible: boolean = false;
  vehicleRequiredErrorVisible: boolean = false;
  vin17RequiredErrorVisible: boolean = false;
  motorcycleNotSupportedErrorVisible: boolean = false;

  private selectedCaseSubscription: Subscription;
  private activeCaseChangedSubscription: Subscription;
  private caseClosedSubscription: Subscription;

  constructor() {
    // attributes from other AWP apps (see NavigationService) or the awp:/ protocol (see ThinClientService)
    this.additionalAttributes = this.location.getState()?.['queryParams'] ?? {};

    // the 'params' parameter in the AWP URL may contain params for web component apps as a JSON object
    if (this.route.snapshot.queryParamMap.get('params')) {
      try {
        const paramsFromUrl: Record<string, string> = JSON.parse(this.route.snapshot.queryParamMap.get('params'));
        Object.assign(this.additionalAttributes, paramsFromUrl);
      } catch (e) {
        console.error("Could not parse 'params' parameter of URL.", e);
      }
    }
  }

  ngOnInit(): void {
    this.appConfiguration = this.route.snapshot.data['appConfiguration'];
    this.initWebComponent();

    this.selectedCaseSubscription = this.serviceCaseHolder.serviceCaseSelected.subscribe(() => {
      this.updateAppVisibility();
    });

    // serviceCaseSelected only fires when the active case actually changes (e.g. switching tabs), so it does not
    // cover the case where the currently active case is enriched in place (e.g. a VIN17 is added to it via the
    // "Add vehicle" overlay). Without this subscription, an app whose vehicle/VIN17 requirement was not fulfilled
    // when it was first shown would keep displaying its error message until the case was reselected or the app
    // reloaded, even though the requirement became fulfilled afterwards.
    this.activeCaseChangedSubscription = this.serviceCaseHolder.activeServiceCaseChanged.subscribe(() => {
      this.updateAppVisibility();
    });

    this.caseClosedSubscription = this.serviceCaseHolder.serviceCaseClosed.subscribe((serviceCaseId: string) => {
      this.webComponentService.deleteWebComponentsOfCase(serviceCaseId);
    });
  }

  ngOnDestroy(): void {
    this.selectedCaseSubscription?.unsubscribe();
    this.activeCaseChangedSubscription?.unsubscribe();
    this.caseClosedSubscription?.unsubscribe();
    this.webComponentService.hideAllApps();
  }

  /**
   * This will load the web component source(s) and add the component to the DOM. It will not be shown yet.
   */
  private initWebComponent(): void {
    this.showLoadingIndicator();
    this.webComponentService.initApp(
      this.appConfiguration,
      this.route.snapshot.data['webComponentUrls'],
      this.route.snapshot.data['getEventListeners'],
      this.route.snapshot.data['getAttributes']
    )
      .catch(this.showErrorIndicator.bind(this))
      .finally(this.hideLoadingIndicator.bind(this));
  }

  /**
   * Set the current app visible if there are no errors and web component has loaded, or hide all apps otherwise.
   */
  private updateAppVisibility(): void {
    this.updateErrorAndLoadingIndicators();
    if (this.showErrorOrLoadingIndicator) {
      this.webComponentService.hideAllApps();
    } else {
      this.webComponentService.showApp(
        this.serviceCaseHolder.getActiveCase()?.getExternalId(),
        this.appConfiguration,
        this.additionalAttributes
      );
    }
  }

  /**
   * Show an error that web component source could not be loaded.
   */
  private showErrorIndicator(): void {
    this.errorIndicatorVisible = true;
    this.updateAppVisibility();
  }

  /**
   * Hide the web component source loading indicator.
   */
  private hideLoadingIndicator(): void {
    this.loadingIndicatorVisible = false;
    this.updateAppVisibility();
  }

  /**
   * Show the web component source loading indicator.
   */
  private showLoadingIndicator(): void {
    this.loadingIndicatorVisible = true;
    this.updateAppVisibility();
  }

  /**
   * Check if any errors or the loading indicator should be shown. If not, this.showErrorOrLoadingIndicator is set to
   * false.
   */
  private updateErrorAndLoadingIndicators(): void {
    const activeCase = this.serviceCaseHolder.getActiveCase();
    const serviceCaseAbsent = !activeCase;
    const vehicleAbsent = !VehicleLoader.isValidVin7(activeCase?.getVinShort())
      && !VehicleLoader.isValidTypeCode(activeCase?.getTypeCode());
    const vin17Absent = !VehicleLoader.isValidVin17(activeCase?.getVinLong());
    const isMotorcycle: boolean = activeCase?.getVehicle()?.getVehicleType() === VehicleType.MOTORCYCLE || activeCase?.getBrand() === Brand.MOT;
    this.serviceCaseRequiredErrorVisible = !!this.appConfiguration.serviceCaseRequired && serviceCaseAbsent;
    this.vehicleRequiredErrorVisible = !!this.appConfiguration.vehicleRequired && vehicleAbsent;
    this.vin17RequiredErrorVisible = !!this.appConfiguration.vinLongRequired && vin17Absent;
    this.motorcycleNotSupportedErrorVisible = !!this.appConfiguration.motorcycleNotSupported && isMotorcycle;

    this.showErrorOrLoadingIndicator = this.serviceCaseRequiredErrorVisible || this.vin17RequiredErrorVisible
      || this.vehicleRequiredErrorVisible || this.motorcycleNotSupportedErrorVisible || this.errorIndicatorVisible
      || this.loadingIndicatorVisible;
  }
}
